import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";

import { clearStoredRoom } from "@/features/room/roomStorage";
import { type Locale, useTranslations } from "@/i18n";
import { theme } from "@/ui/theme";

// ── Styled ───────────────────────────────────────────────────

const Wrap = styled.div`
  position: relative;
`;

const IconBtn = styled.button`
  all: unset;
  cursor: pointer;
  width: 2.25rem;
  height: 2.25rem;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.xl};
  background: ${theme.gradients.primary};
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
  color: ${theme.colors.primaryFg};
  box-shadow: ${theme.shadow.glow};
  transition: transform 0.15s;
  &:active {
    transform: scale(0.94);
  }
`;

const Menu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  min-width: 7rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.bgCard};
  box-shadow: ${theme.shadow.card};
  overflow: hidden;
  backdrop-filter: blur(12px);
`;

const Item = styled.button<{ active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: ${(p) => (p.active ? 600 : 400)};
  color: ${(p) => (p.active ? theme.colors.primary : theme.colors.text)};
  background: ${(p) =>
    p.active ? `color-mix(in oklab, ${theme.colors.primary} 8%, transparent)` : "transparent"};
  transition: background 0.12s;
  &:hover {
    background: color-mix(in oklab, ${theme.colors.primary} 12%, transparent);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
  margin: 0.25rem 0;
`;

const LOCALE_OPTIONS: { id: Locale; flag: string; label: string }[] = [
  { id: "en", flag: "🇬🇧", label: "English" },
  { id: "fr", flag: "🇧🇪", label: "Français" },
  { id: "vl", flag: "🇧🇪", label: "Vlaams" },
];

// ── Component ────────────────────────────────────────────────

export const BrandMenu = ({ icon = "🔥" }: { icon?: string }) => {
  const { locale, setLocale, t } = useTranslations();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  return (
    <Wrap ref={ref}>
      <IconBtn onClick={() => setOpen((v) => !v)} aria-label="Menu">
        {icon}
      </IconBtn>
      <AnimatePresence>
        {open && (
          <Menu
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {LOCALE_OPTIONS.map((opt) => (
              <Item
                key={opt.id}
                active={locale === opt.id}
                onClick={() => {
                  setLocale(opt.id);
                  setOpen(false);
                }}
              >
                <span>{opt.flag}</span> {opt.label}
              </Item>
            ))}
            <Divider />
            <Item
              onClick={() => {
                clearStoredRoom();
                window.location.reload();
              }}
            >
              <span>🚪</span> {t("arena.leaveRoom")}
            </Item>
          </Menu>
        )}
      </AnimatePresence>
    </Wrap>
  );
};
